import type { KnowledgeListFilters } from "@intelligems/ig-types/schemas/ai/knowledge";
import { AnalyzeExperienceFrontendTinybirdRequestBodyType } from "@intelligems/ig-types/schemas/analytics/analyze-experience-tinybird";
import { AnalyzeSitewideFrontendTinybirdRequestBodyType } from "@intelligems/ig-types/schemas/analytics/analyze-sitewide";
import { AssetTypeType } from "@intelligems/ig-types/schemas/organization";
import { DeviceType } from "@intelligems/ig-types/schemas/analytics/base";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { ExperienceDto } from "@intelligems/ig-types/dtos/experiences";
import { ExperienceModule } from "@intelligems/ig-types/dtos/experience-modules";
import { GetExperiencesListResponseBodyType } from "@intelligems/ig-types/functions/experiences/get-experiences-list/get-experiences-list";
import { GetExperiencesResponseBodyType } from "@intelligems/ig-types/functions/experiences/get-experiences";
import { PutExperienceRequestBodyType } from "@intelligems/ig-types/functions/experiences/put-experience";
import { InfiniteData, QueryClient } from "@tanstack/react-query";

export const organizationsKeys = {
  all: [{ scope: "organizations" }] as const,
  one: [{ scope: "organizations" }] as const,
  orgSummary: (orgId: string) =>
    [{ scope: "organizations", orgSummary: true, orgId }] as const,
  orgSummaries: (params?: {
    limit?: number;
    offset?: number;
    query?: string;
  }) => [{ scope: "organizations", orgSummaries: true, ...params }] as const,
  stripeSubscriptionLinks: [
    { scope: "organizations", stripeSubscriptionLinks: true },
  ] as const,
  billingUrl: (orgId: string) => [
    { ...organizationsKeys.all[0], billingUrl: orgId },
  ],
  webhooks: (orgId: string) =>
    [{ scopes: "organizations", orgId, webhooks: true }] as const,
  scriptTagEnabled: (orgId: string) => [
    { ...organizationsKeys.all[0], scriptTagEnabled: orgId },
  ],
  shopPages: (orgId: string) => [
    { ...organizationsKeys.all[0], orgId, pages: true },
  ],
  cartTransforms: (orgId: string) => [
    { ...organizationsKeys.all[0], orgId, cartTransforms: true },
  ],
  metafield: (
    orgId: string,
    params?: { type: string; id: string; key: string }
  ) => [{ ...organizationsKeys.all[0], orgId, metafield: true, ...params }],
} as const;

export const pluginPerformanceKeys = {
  one: (orgId: string) => [{ scope: "pluginPerformance", orgId }],
} as const;

export const adminKeys = {
  agencies: [{ scope: "admin", agencies: true }] as const,
  agency: (id: string) =>
    [{ scope: "admin", agencies: true, type: "singleAgency", id }] as const,
  adminOrgSummary: (orgId: string) =>
    [{ scope: "admin", adminOrgSummary: true, orgId }] as const,
  meterLogs: (subscriptionLinkId: string) =>
    [{ scope: "admin", meterLogs: true, subscriptionLinkId }] as const,
  agencyCustomers: (agencyId: string) =>
    [{ scope: "admin", agencyCustomers: true, agencyId }] as const,
  organizationAgencyLink: (agencyId: string) =>
    [{ scope: "admin", organizationAgencyLink: true, agencyId }] as const,
  stripeSubscriptionLinks: (orgId: string) =>
    [{ scope: "admin", stripeSubscriptionLinks: true, orgId }] as const,
  previewBrandPlanOverages: (agencyId: string, brandId: string) =>
    [
      {
        scope: "admin",
        previewBrandPlanOverages: true,
        agencyId,
        brandId,
      },
    ] as const,
  getPendingAgencyDowngrades: (agencyId: string) => [
    {
      scope: "admin",
      getPendingAgencyDowngrades: true,
      agencyId,
    },
  ],
  getPendingAgencyCancellations: (agencyId: string) => [
    {
      scope: "admin",
      getPendingAgencyCancellations: true,
      agencyId,
    },
  ],
} as const;

export const intelligemsKeys = {
  livePrices: [{ scope: "intelligems", livePrices: true }] as const,
} as const;

export const pluginPendingTestKeys = {
  one: (testId: string) => [{ scope: "pendingPluginTest", testId }],
} as const;

export const userKeys = {
  all: [{ scope: "user" }] as const,
  hubspotToken: ["hubspotToken"],
  org: (orgId: string) => [{ scope: "user", orgId }],
};

export const subscriptionProductKeys = {
  all: [{ scope: "subscriptionProducts" }] as const,
  one: (orgId: string) => [{ scope: "subscriptionProducts", orgId }],
} as const;

export const billingModulesKeys = {
  all: [{ scope: "billingModules" }] as const,
  subscriptionAccess: (orgId: string) =>
    [
      {
        ...billingModulesKeys.all[0],
        subscriptionAccess: true,
        orgId,
      },
    ] as const,
  adminSubscriptionAccess: (orgId: string) =>
    [
      {
        ...billingModulesKeys.all[0],
        adminSubscriptionAccess: true,
        orgId,
      },
    ] as const,
  pastSubscriptionAmounts: (orgId: string) =>
    [
      {
        ...billingModulesKeys.all[0],
        pastSubscriptionAmounts: true,
        orgId,
      },
    ] as const,
} as const;

export const billingEntitlementsKeys = {
  all: [{ scope: "billingEntitlements" }] as const,
  one: (id: string) => [{ ...billingEntitlementsKeys.all[0], id }] as const,
} as const;

export const billingWorkflowKeys = {
  all: [{ scope: "billingWorkflow" }] as const,
  adminCommand: [{ scope: "billingWorkflow", adminCommand: true }] as const,
  selfServeCommand: [
    { scope: "billingWorkflow", selfServeCommand: true },
  ] as const,
  selfServeRecoveryReplay: [
    { scope: "billingWorkflow", selfServeRecoveryReplay: true },
  ] as const,
  agencyCommand: [{ scope: "billingWorkflow", agencyCommand: true }] as const,
} as const;

export const processesKeys = {
  all: [{ scope: "processes" }] as const,
  one: (orgId: string) => [{ scope: "processes", orgId }],
};

export const screenshotKeys = {
  all: [{ scope: "screenshot" }] as const,
  fresh: () => [{ ...screenshotKeys.all[0], fresh: true }],
  org: (orgId: string) => [{ ...screenshotKeys.all[0], orgId }],
  one: (orgId: string, path: string, device: DeviceType, fresh: boolean) => [
    { ...screenshotKeys.all[0], path, orgId, device, fresh },
  ],
};

export const subscriptionsKeys = {
  all: (orgId: string) => [{ scope: "activeSubscriptions", orgId }] as const,
};

export const organizationDomainKey = {
  one: (orgId: string) => [{ scope: "organizationDomain", orgId }] as const,
};

export const aiFeedbackKeys = {
  all: [{ scope: "aiFeedback" }] as const,
  one: (sessionId: string) => [{ scope: "aiFeedback", sessionId }] as const,
  many: (sessionIds: string[]) =>
    [{ scope: "aiFeedback", sessionIds: sessionIds.join(",") }] as const,
};

export const aiSitewideAnalyticsKeys = {
  chatHistory: (orgId: string) =>
    [{ scope: "aiSitewideAnalyticsChatHistory", orgId }] as const,
  chatSessionHistory: (orgId: string, sessionId: string) =>
    [
      {
        scope: "aiSitewideAnalyticsChatSessionHistory",
        orgId,
        sessionId,
      },
    ] as const,
};

export const aiExperimentAnalyticsV2Keys = {
  chatHistory: (experienceId: string) =>
    [{ scope: "aiExperimentAnalyticsV2ChatHistory", experienceId }] as const,
  chatSessionHistory: (sessionId: string) =>
    [
      { scope: "aiExperimentAnalyticsV2ChatSessionHistory", sessionId },
    ] as const,
};

export const aiExperienceCreationChatKeys = {
  // Creation chats aren't tied to a saved experience (the experience has no id
  // until saved), so history is scoped to the user + workflow, not an
  // experience.
  chatHistory: () => [{ scope: "aiExperienceCreationChatHistory" }] as const,
  chatSessionHistory: (sessionId: string) =>
    [{ scope: "aiExperienceCreationChatSessionHistory", sessionId }] as const,
};

export const organizationProductKey = {
  one: (orgId: string, sample: string) =>
    [{ scope: "organizationProduct", orgId, sample }] as const,
};

export const organizationPageViewsKey = {
  one: (orgId: string) => [{ scope: "organizationPageViews", orgId }] as const,
};

export const organizationScriptStatusKey = {
  org: (orgId: string) =>
    [{ scope: "organizationScriptStatus", orgId }] as const,
  preview: (
    orgId: string,
    previewId?: string,
    fullUrl?: string,
    mode?: "quick" | "full"
  ) =>
    [
      {
        ...organizationScriptStatusKey.org(orgId)[0],
        previewId,
        fullUrl,
        mode,
      },
    ] as const,
};

export const organizationThemesKey = {
  one: (orgId: string) => [{ scope: "organizationThemes", orgId }] as const,
};

export const organizationThemeAssetsKey = {
  all: [{ scope: "organizationThemeAssets" }] as const,
  one: (orgId: string, themeId: string, assetType: AssetTypeType) =>
    [
      {
        ...organizationThemeAssetsKey.all[0],
        orgId,
        themeId: themeId || "live",
        assetType,
      },
    ] as const,
};

export const historyKeys = {
  one: (id: string) => [{ scope: "history", id }] as const,
  allConfigEvents: [{ scope: "configEvents" }] as const,
  configEvents: (startTs?: number, endTs?: number) =>
    [{ ...historyKeys.allConfigEvents, startTs, endTs }] as const,
  getConfigCompare: (from: string, to: string) =>
    [{ scope: "configCompare", from, to }] as const,
};

export const gaKeys = {
  all: [{ scope: "ga" }] as const,
  accounts: () => [{ ...gaKeys.all[0], accounts: true }],
  streams: (accountId: string, propertyId: string) => [
    { ...gaKeys.all[0], streams: true, accountId, propertyId },
  ],
  audiences: (accountId: string, propertyId: string) => [
    { ...gaKeys.all[0], audiences: true, accountId, propertyId },
  ],
};

export const integrationKeys = {
  all: [{ scope: "integrations" }] as const,
  metaAdsStatus: () =>
    [{ ...integrationKeys.all[0], metaAdsStatus: true }] as const,
  metaAdsAccounts: () =>
    [{ ...integrationKeys.all[0], metaAdsAccounts: true }] as const,
};

export const slackConfigKeys = {
  all: [{ scope: "slackConfig" }] as const,
  config: [{ scope: "slackConfig", config: true }] as const,
  availableChannels: [
    { scope: "slackConfig", availableChannels: true },
  ] as const,
  igBotAvailableChannels: [
    { scope: "slackConfig", igBotAvailableChannels: true },
  ] as const,
};

export const slackMemberConnectionKeys = {
  all: [{ scope: "slackMemberConnection" }] as const,
  connection: [{ scope: "slackMemberConnection", connection: true }] as const,
};

export const adminSlackKeys = {
  all: [{ scope: "adminSlack" }] as const,
  integrations: [{ scope: "adminSlack", integrations: true }] as const,
  config: (integrationId: string) =>
    [{ scope: "adminSlack", config: true, integrationId }] as const,
  channels: [{ scope: "adminSlack", channels: true }] as const,
};

export const klaviyoKeys = {
  all: [{ scope: "klaviyo" }] as const,
  allSegments: (orgId: string) =>
    [{ scope: "klaviyo", allSegments: true, orgId }] as const,
};

export const uiRecommendationKeys = {
  all: [{ scope: "uiRecommendations" }] as const,
  one: (orgId: string) => [{ scope: "uiRecommendations", orgId }] as const,
};

export const redirectKeys = {
  all: [{ scope: "redirects" }] as const,
  byExperience: (experienceId: string) =>
    [{ scope: "redirects", experienceId }] as const,
};

export const experienceKeys = {
  experiencesListRoot: [{ scope: "experiences-list" }] as const,
  experiencesList: (params?: {
    category?: ExperienceCategory;
    module?: ExperienceModule;
  }) => [{ ...experienceKeys.experiencesListRoot[0], ...params }] as const,
  experimentList: [{ scope: "experiences-list-experiment" }] as const,
  internalExperimentList: [
    { scope: "internal-experiences-list-experiment" },
  ] as const,
  // HomeDashboard & onboarding - uses ExperienceListDto (lighter, includes history)
  homeOverviewPersonalizationList: [
    { scope: "experiences-list-personalization" },
  ] as const,
  // SiteContent & Personalizations overview - uses full ExperienceDto
  detailedPersonalizationList: [
    { scope: "experiences-personalization" },
  ] as const,
  // Offers page - server-filtered to only experiences with offers
  offerPersonalizationList: [
    { scope: "experiences-offerPersonalization" },
  ] as const,
  // Checkout blocks page - server-filtered to only experiences with checkout blocks
  checkoutPersonalizationList: [
    { scope: "experiences-checkoutPersonalization" },
  ] as const,
  // Post-purchase page - server-filtered to only experiences with post-purchase blocks
  postPurchasePersonalizationList: [
    { scope: "experiences-postPurchasePersonalization" },
  ] as const,
  all: [{ scope: "experiences" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "experience",
        experienceId: experienceId,
      },
    ] as const,
  /**
   * The source-experience read (`getSourceExperience`) has a different contract
   * from `one`: it resolves to `null` when the source is deleted or 404s, where
   * `one` promises a `GetExperienceResponseBodyType` and rejects. Sharing one
   * cache entry made the two contracts overwrite each other — a cached `null`
   * was handed to `useGetExperience` consumers that dereference it, and
   * `getExperience` taking over the entry turned a deleted source back into a
   * rejected 404 that reached Sentry instead of being swallowed. `variant` keeps
   * the entries apart while staying a superset of `one`, so the existing
   * `invalidateQueries({ queryKey: experienceKeys.one(id) })` calls — which
   * match query keys partially — keep refreshing it.
   */
  source: (experienceId: string) =>
    [
      {
        ...experienceKeys.one(experienceId)[0],
        variant: "source",
      },
    ] as const,
};

export const setExperienceQueryData = (
  queryClient: QueryClient,
  experience: ExperienceDto | PutExperienceRequestBodyType["experience"]
) => {
  if (!experience.id) {
    return;
  }

  const data = { experience };
  queryClient.setQueryData(experienceKeys.one(experience.id), data);

  const sourceKey = experienceKeys.source(experience.id);
  if (queryClient.getQueryState(sourceKey)) {
    queryClient.setQueryData(sourceKey, experience.isDeleted ? null : data);
  }
};

export const experienceFileKeys = {
  shopifyMarketsCatalogs: [
    { scope: "experienceFiles", shopifyMarketsCatalogs: true },
  ] as const,
};

export const invalidateModuleExperienceListQueries = (
  queryClient: QueryClient
) => {
  void queryClient.invalidateQueries({
    queryKey: experienceKeys.experiencesListRoot,
  });
};

export const s3FilesKeys = {
  all: ["s3-files"] as const,
  download: (filePath: string, bucket?: string) =>
    [...s3FilesKeys.all, "download", filePath, bucket] as const,
};

/** Updates an experience in a cache that uses the `experiences` field */
export const updateInExperiencesCache = (
  queryClient: QueryClient,
  queryKey: readonly [{ scope: string }],
  updatedExperience: ExperienceDto
) => {
  queryClient.setQueryData<InfiniteData<GetExperiencesResponseBodyType>>(
    queryKey,
    (old) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          experiences: page.experiences.map((exp) =>
            exp.id === updatedExperience.id ? updatedExperience : exp
          ),
        })),
      };
    }
  );
};

/** Removes an experience from a cache that uses the `experiences` field */
export const removeFromExperiencesCache = (
  queryClient: QueryClient,
  queryKey: readonly [{ scope: string }],
  experienceId: string
) => {
  queryClient.setQueryData<InfiniteData<GetExperiencesResponseBodyType>>(
    queryKey,
    (old) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          experiences: page.experiences.filter(
            (exp) => exp.id !== experienceId
          ),
        })),
      };
    }
  );
};

/** Updates an experience in a cache that uses the `experiencesList` field */
export const updateInExperiencesListCache = (
  queryClient: QueryClient,
  queryKey: readonly [{ scope: string }],
  updatedExperience: ExperienceDto
) => {
  queryClient.setQueryData<InfiniteData<GetExperiencesListResponseBodyType>>(
    queryKey,
    (old) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          experiencesList: page.experiencesList.map((exp) =>
            exp.id === updatedExperience.id
              ? ({ ...exp, ...updatedExperience } as typeof exp)
              : exp
          ),
        })),
      };
    }
  );
};

/** Removes an experience from a cache that uses the `experiencesList` field */
export const removeFromExperiencesListCache = (
  queryClient: QueryClient,
  queryKey: readonly [{ scope: string }],
  experienceId: string
) => {
  queryClient.setQueryData<InfiniteData<GetExperiencesListResponseBodyType>>(
    queryKey,
    (old) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          experiencesList: page.experiencesList.filter(
            (exp) => exp.id !== experienceId
          ),
        })),
      };
    }
  );
};

/** Adds an experience to a cache that uses the `experiences` field (prepends to first page) */
export const addToExperiencesCache = (
  queryClient: QueryClient,
  queryKey: readonly [{ scope: string }],
  newExperience: ExperienceDto
) => {
  queryClient.setQueryData<InfiniteData<GetExperiencesResponseBodyType>>(
    queryKey,
    (old) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map((page, index) =>
          index === 0
            ? { ...page, experiences: [newExperience, ...page.experiences] }
            : page
        ),
      };
    }
  );
};

/** Adds an experience to a cache that uses the `experiencesList` field (prepends to first page) */
export const addToExperiencesListCache = (
  queryClient: QueryClient,
  queryKey: readonly [{ scope: string }],
  newExperience: ExperienceDto
) => {
  queryClient.setQueryData<InfiniteData<GetExperiencesListResponseBodyType>>(
    queryKey,
    (old) => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map((page, index) =>
          index === 0
            ? {
                ...page,
                experiencesList: [
                  newExperience as unknown as GetExperiencesListResponseBodyType["experiencesList"][number],
                  ...page.experiencesList,
                ],
              }
            : page
        ),
      };
    }
  );
};

export const shareTokenKeys = {
  all: [{ scope: "shareTokens" }] as const,
  one: (experienceId: string) =>
    [{ scope: "shareTokens", experienceId }] as const,
};

export const customEventsKeys = {
  all: [{ scope: "customEvents" }] as const,
  one: (eventId: string) =>
    [
      {
        scope: "customEvents",
        eventId: eventId,
      },
    ] as const,
};

export const combinedCustomEventsKeys = {
  all: [{ scope: "combinedCustomEvents" }] as const,
  one: (eventId: string) =>
    [
      {
        scope: "combinedCustomEvents",
        eventId: eventId,
      },
    ] as const,
};

export const experienceKeyMetricsKeys = {
  one: (experienceId: string) =>
    [{ scope: "experienceKeyMetrics", experienceId }] as const,
};

export const scheduleKeys = {
  all: [{ scope: "schedules" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "schedules",
        experienceId: experienceId,
      },
    ] as const,
};

export const checkoutBlockKeys = {
  all: [{ scope: "checkoutBlocks" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "checkoutBlock",
        experienceId: experienceId,
      },
    ] as const,
};

export const exclusionGroupKeys = {
  all: [{ scope: "exclusionGroups" }] as const,
  one: (exclusionGroupId: string) =>
    [{ scope: "exclusionGroup", exclusionGroupId }] as const,
};

export const experienceProductsKeys = {
  all: [{ scope: "experienceProducts" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "experienceProducts",
        experienceId: experienceId,
      },
    ] as const,
};

export const analyticsProductsKeys = {
  all: [{ scope: "analyticsProducts" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "analyticsProducts",
        experienceId: experienceId,
      },
    ] as const,
};

export const offerProductsKeys = {
  all: [{ scope: "offerProducts" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "offerProducts",
        experienceId: experienceId,
      },
    ] as const,
};

export const aiKeys = {
  all: [{ scope: "ai" }] as const,
  one: (orgId: string, type: "onsite-injections") => [
    { scope: "ai", orgId, type },
  ],
  contextCategories: [
    { scope: "ai", context: true, categories: true },
  ] as const,
  contextFile: (category: string, name: string) =>
    [{ ...aiKeys.contextCategories, category, name }] as const,
  tools: [{ scope: "ai", tools: true }],
  testCategorization: (id: string) => [
    { scope: "ai", testCategorization: true, id },
  ],
  testCategorizationJob: (jobId: string) => [
    { scope: "ai", categorizationJob: true, jobId },
  ],
  categorizedExperiments: (opts: {
    random?: number;
    enabled?: boolean;
    orgId?: string;
  }) => [{ scope: "ai", categorizedExperiments: true, ...opts }],
  qaCurrentRun: (experienceId: string) =>
    [{ scope: "ai", qaCurrentRun: true, experienceId }] as const,
  qaEligibility: (experienceId: string) =>
    [{ scope: "ai", qaEligibility: true, experienceId }] as const,
  qaAdminRuns: (params: { page: number; limit: number }) =>
    [{ scope: "ai", qaAdminRuns: true, ...params }] as const,
  qaAdminRunDetails: (runId: string) =>
    [{ scope: "ai", qaAdminRunDetails: true, runId }] as const,
  qaRunDetails: (runId: string) =>
    [{ scope: "ai", qaRunDetails: true, runId }] as const,
  categorizationsList: (params: {
    page: number;
    limit: number;
    search?: string;
    testTypes?: string;
    isRandom?: number;
    verificationFilter?: "verified" | "unverified";
    orgId?: string;
  }) => [{ scope: "ai", categorizationsList: true, ...params }],
  // Called with no params it returns the bare prefix key for params-agnostic
  // invalidation of every classification-list query; with params it keys one page.
  experimentClassifications: (params?: Record<string, unknown>) =>
    [{ scope: "ai", experimentClassifications: true, ...params }] as const,
  experimentClassificationDetail: (experienceId: string) =>
    [
      { scope: "ai", experimentClassificationDetail: true, experienceId },
    ] as const,
  // Called with no params it returns the bare prefix key for params-agnostic
  // invalidation of every run-health query; with params it keys one page.
  experimentClassificationRuns: (params?: Record<string, unknown>) =>
    [{ scope: "ai", experimentClassificationRuns: true, ...params }] as const,
};

export const knowledgeKeys = {
  all: [{ scope: "knowledge" }] as const,
  list: (filters: Partial<KnowledgeListFilters>) =>
    [{ ...knowledgeKeys.all[0], list: true, ...filters }] as const,
  categories: (source?: string) =>
    [{ ...knowledgeKeys.all[0], categories: true, source }] as const,
  symptoms: (source?: string) =>
    [{ ...knowledgeKeys.all[0], symptoms: true, source }] as const,
  rootCauses: (source?: string) =>
    [{ ...knowledgeKeys.all[0], rootCauses: true, source }] as const,
  stats: (source?: string) =>
    [{ ...knowledgeKeys.all[0], stats: true, source }] as const,
  document: (id: string) => [{ ...knowledgeKeys.all[0], id }] as const,
  content: (id: string) =>
    [{ ...knowledgeKeys.all[0], id, content: true }] as const,
};

export const workflowKeys = {
  all: [{ scope: "workflow" }] as const,
  one: (sessionId: string) => [{ ...workflowKeys.all[0], sessionId }],
  sessionSteps: (sessionId: string) =>
    [{ ...workflowKeys.all[0], sessionId, steps: true }] as const,
  nodeRuns: (workflowId: string, nodeId: string) =>
    [{ ...workflowKeys.all[0], workflowId, nodeId, runs: true }] as const,
  workflowSessions: (workflowId: string) =>
    [{ ...workflowKeys.all[0], workflowId, sessions: true }] as const,
};

export const pylonKeys = {
  all: [{ scope: "pylon" }] as const,
  ticketAnalysis: (issueId: string) =>
    [{ scope: "pylon", ticketAnalysis: true, issueId }] as const,
};

export const shiftKeys = {
  all: [{ scope: "shift" }] as const,
  ticketAnalysis: (linearIssueId: string) =>
    [{ scope: "shift", ticketAnalysis: true, linearIssueId }] as const,
};

export const discountKeys = {
  all: [{ scope: "discounts" }] as const,
  selected: (discountId: string) =>
    [{ ...discountKeys.all[0], discountId: discountId }] as const,
};

export const shippingProfileBackupKeys = {
  all: [{ scope: "shippingProfileBackups" }] as const,
  selected: (experienceId: string) =>
    [{ ...shippingProfileBackupKeys.all[0], experienceId }] as const,
};

export const userInterfaceKeys = {
  all: [{ scope: "userInterfaces" }] as const,
  selected: (variationId: string) => [
    { ...userInterfaceKeys.all[0], userInterfaceId: variationId },
  ],
};

export const widgetKeys = {
  all: [{ scope: "widgets" }] as const,
  selected: (widgetId: string) =>
    [{ ...widgetKeys.all[0], widgetId: widgetId }] as const,
};

export const orgListKeys = {
  all: [{ scope: "orgList" }] as const,
  search: (params: { limit: number; offset: number; query?: string }) =>
    [{ ...orgListKeys.all[0], ...params }] as const,
};

export const offerKeys = {
  all: [{ scope: "offers" }] as const,
  experiencesList: [{ scope: "offers", type: "experiencesList" }] as const,
};

export const tinybirdEvents = {
  all: [{ scope: "tinybirdEvents" }] as const,
  summaryMetrics: [
    { scope: "tinybirdEvents", type: "summaryMetrics" },
  ] as const,
};

export const unregisteredTinybirdEvents = {
  all: [{ scope: "unregisteredTinybirdEvents" }] as const,
};

export const sitewideAnalyticsKeys = {
  all: (data: AnalyzeSitewideFrontendTinybirdRequestBodyType) =>
    [
      {
        type: "sitewideAnalytics",
        ...dashboardKeys.all[0],
        ...data,
      },
    ] as const,
};

export const dashboardKeys = {
  all: [{ topic: "dashboards" }] as const,

  selectedTinybirdExperiment: (
    data: AnalyzeExperienceFrontendTinybirdRequestBodyType
  ) =>
    [
      {
        selected: true,
        type: "experiencesTinybird",
        ...dashboardKeys.all[0],
        ...data,
      },
    ] as const,
};

export const personalizationKeys = {
  all: (includeDiscount: boolean) =>
    [{ scope: "personalizations", includeDiscount }] as const,
  selected: (personalizationId: string) =>
    [{ ...personalizationKeys.all[0], campaignId: personalizationId }] as const,
  one: (personalizationId: string) =>
    [{ scope: "personalization", campaignId: personalizationId }] as const,
};

export const shippingProviderKeys = {
  all: [{ scope: "shippingProviders" }] as const,
  one: (experienceId: string) =>
    [{ ...shippingProviderKeys.all[0], experienceId }] as const,
  source: (experienceId: string, sourceUrl: string | null) =>
    [
      { ...shippingProviderKeys.all[0], experienceId, sourceUrl, source: true },
    ] as const,
  publishJob: (experienceId: string, jobId: string) =>
    [
      {
        ...shippingProviderKeys.all[0],
        experienceId,
        jobId,
        publishJob: true,
      },
    ] as const,
  versions: (experienceId: string) =>
    [{ ...shippingProviderKeys.all[0], experienceId, versions: true }] as const,
  versionDetail: (experienceId: string, version: number) =>
    [
      {
        ...shippingProviderKeys.all[0],
        experienceId,
        version,
        versionDetail: true,
      },
    ] as const,
  runs: (
    experienceId: string,
    params: { type?: "run" | "replay"; limit?: number }
  ) =>
    [
      { ...shippingProviderKeys.all[0], experienceId, runs: true, ...params },
    ] as const,
  runDetail: (experienceId: string, eventId: string) =>
    [
      {
        ...shippingProviderKeys.all[0],
        experienceId,
        eventId,
        runDetail: true,
      },
    ] as const,
  replays: (experienceId: string, params: { limit?: number }) =>
    [
      {
        ...shippingProviderKeys.all[0],
        experienceId,
        replays: true,
        ...params,
      },
    ] as const,
  scenarioRequest: (scenarioId: string, requestUrl: string | null) =>
    [
      {
        ...shippingProviderKeys.all[0],
        scenarioId,
        requestUrl,
        scenarioRequest: true,
      },
    ] as const,
} as const;

export const agenticLogicKeys = {
  all: [{ scope: "agenticLogic" }] as const,
  templates: () => [{ ...agenticLogicKeys.all[0], templates: true }] as const,
  templateSource: (templateId: string) =>
    [{ ...agenticLogicKeys.all[0], templateId, templateSource: true }] as const,
  rules: () => [{ ...agenticLogicKeys.all[0], rules: true }] as const,
  systemRule: (systemKey: string) =>
    [{ ...agenticLogicKeys.all[0], systemKey, systemRule: true }] as const,
  rule: (ruleId: string) =>
    [{ ...agenticLogicKeys.all[0], ruleId, rule: true }] as const,
  draft: (ruleId: string) =>
    [{ ...agenticLogicKeys.all[0], ruleId, draft: true }] as const,
  versions: (ruleId: string) =>
    [{ ...agenticLogicKeys.all[0], ruleId, versions: true }] as const,
  scenarios: (ruleId: string) =>
    [{ ...agenticLogicKeys.all[0], ruleId, scenarios: true }] as const,
  generatedTests: (ruleId: string) =>
    [{ ...agenticLogicKeys.all[0], ruleId, generatedTests: true }] as const,
  scenarioEvaluation: (ruleId: string, scenarioName: string) =>
    [
      {
        ...agenticLogicKeys.all[0],
        ruleId,
        scenarioName,
        scenarioEvaluation: true,
      },
    ] as const,
  runs: (ruleId: string, limit: number, source: "live" | "history") =>
    [
      { ...agenticLogicKeys.all[0], ruleId, limit, source, runs: true },
    ] as const,
  run: (ruleId: string, invocationId: string) =>
    [{ ...agenticLogicKeys.all[0], ruleId, invocationId, run: true }] as const,
  versionDetail: (ruleId: string, version: number) =>
    [
      {
        ...agenticLogicKeys.all[0],
        ruleId,
        version,
        versionDetail: true,
      },
    ] as const,
  publishJob: (ruleId: string, jobId: string) =>
    [
      {
        ...agenticLogicKeys.all[0],
        ruleId,
        jobId,
        publishJob: true,
      },
    ] as const,
  /** Every publish job of one rule — the prefix of {@link publishJob}. */
  publishJobs: (ruleId: string) =>
    [{ ...agenticLogicKeys.all[0], ruleId, publishJob: true }] as const,
  placements: () => [{ ...agenticLogicKeys.all[0], placements: true }] as const,
  assignments: () =>
    [{ ...agenticLogicKeys.all[0], assignments: true }] as const,
  exclusions: () => [{ ...agenticLogicKeys.all[0], exclusions: true }] as const,
  datasets: (namespace: string | undefined) =>
    [{ ...agenticLogicKeys.all[0], namespace, datasets: true }] as const,
  // Every dataset list regardless of namespace filter — what a write
  // invalidates, since a new dataset can land in any namespace.
  allDatasets: () => [{ ...agenticLogicKeys.all[0], datasets: true }] as const,
  dataset: (key: string) =>
    [{ ...agenticLogicKeys.all[0], key, dataset: true }] as const,
  sources: () => [{ ...agenticLogicKeys.all[0], sources: true }] as const,
  source: (name: string) =>
    [{ ...agenticLogicKeys.all[0], name, source: true }] as const,
  sourceOutput: (name: string) =>
    [{ ...agenticLogicKeys.all[0], name, sourceOutput: true }] as const,
  // A MUTATION key: "Refresh now" is keyed so the Sources list can watch
  // the mutation cache for refreshes it should poll for.
  sourceRefresh: () =>
    [{ ...agenticLogicKeys.all[0], sourceRefresh: true }] as const,
  // Fleet-wide (every org) stats for the Intelligems admin dashboard.
  fleetStats: (windowDays: number) =>
    [{ ...agenticLogicKeys.all[0], windowDays, fleetStats: true }] as const,
  fleetOrgNames: (orgIds: readonly string[]) =>
    [{ ...agenticLogicKeys.all[0], orgIds, fleetOrgNames: true }] as const,
  fleetUpstash: () =>
    [{ ...agenticLogicKeys.all[0], fleetUpstash: true }] as const,
  fleetAuthoringStats: (windowDays: number) =>
    [
      { ...agenticLogicKeys.all[0], windowDays, fleetAuthoringStats: true },
    ] as const,
} as const;

export const shippingCarrierKeys = {
  all: [{ scope: "shippingCarriers" }] as const,
  current: () => [{ ...shippingCarrierKeys.all[0], current: true }] as const,
} as const;

export const orgSecretKeys = {
  all: [{ scope: "orgSecrets" }] as const,
} as const;

export const demoStoresKeys = {
  snapshots: [{ scope: "demoStores", snapshots: true }] as const,
  resetJobs: (limit: number) =>
    [{ scope: "demoStores", resetJobs: true, limit }] as const,
  allJobs: (limit: number) =>
    [{ scope: "demoStores", allJobs: true, limit }] as const,
  promoteJobs: (limit: number) =>
    [{ scope: "demoStores", promoteJobs: true, limit }] as const,
  mappings: [{ scope: "demoStores", mappings: true }] as const,
  stagingExperiences: [
    { scope: "demoStores", stagingExperiences: true },
  ] as const,
} as const;
